import React, { useState, useEffect } from 'react';
import { useSite } from '../../context/SiteContext';
import { SectionRenderer } from './SectionRenderer';
import { FloatingCtaBar } from '../common/FloatingCtaBar';
import { PlatformAttribution } from '../common/PlatformAttribution';
import { HeaderSettingsModal } from './HeaderSettingsModal';
import { ImagePickerModal } from './ImagePickerModal';
import { getSiteThemeVariables } from '../../utils/contrast';
import {
  ChevronUp,
  ChevronDown,
  Copy,
  Trash2,
  Plus,
  Sliders,
  Sparkles,
  Edit3,
} from 'lucide-react';

interface EditorCanvasProps {
  onOpenAddSectionModal: (index?: number) => void;
  onOpenProperties?: () => void;
}

export const EditorCanvas: React.FC<EditorCanvasProps> = ({
  onOpenAddSectionModal,
  onOpenProperties,
}) => {
  const {
    activeSite,
    activePageId,
    setActivePageId,
    selectedSectionId,
    setSelectedSectionId,
    deviceMode,
    testViewportWidth,
    isPreviewMode,
    moveSectionInActivePage,
    duplicateSectionInActivePage,
    deleteSectionInActivePage,
    updateSectionInActivePage,
    updateSiteData,
  } = useSite();

  const [isHeaderSettingsOpen, setIsHeaderSettingsOpen] = useState(false);
  const [isImagePickerOpen, setIsImagePickerOpen] = useState(false);
  const [imagePickerTargetSectionId, setImagePickerTargetSectionId] = useState<string | null>(null);
  const [animPreviewKeys, setAnimPreviewKeys] = useState<Record<string, number>>({});

  useEffect(() => {
    const handleAnimPreview = (e: any) => {
      const secId = e.detail?.sectionId;
      if (secId) {
        setAnimPreviewKeys((prev) => ({ ...prev, [secId]: Date.now() }));
      }
    };
    window.addEventListener('adiosoft-preview-anim', handleAnimPreview);
    return () => window.removeEventListener('adiosoft-preview-anim', handleAnimPreview);
  }, []);

  const activePage = activeSite?.pages?.find((p) => p && p.id === activePageId) || activeSite?.pages?.find((p) => p && p.isHomePage) || activeSite?.pages?.[0];
  const pageSections = activePage?.sections || [];

  useEffect(() => {
    if (activePage && activePage.id && activePage.id !== activePageId) {
      setActivePageId(activePage.id);
    }
  }, [activePage, activePageId, setActivePageId]);

  if (!activeSite) return null;

  const handleCanvasNavigate = (slug: string) => {
    if (!activeSite?.pages || !slug) return;
    const cleanSlug = slug.startsWith('/') ? slug : `/${slug}`;
    const targetPage = activeSite.pages.find(
      (p) =>
        p &&
        (p.slug === cleanSlug ||
          p.slug === slug ||
          p.id === slug ||
          (cleanSlug === '/' && p.isHomePage))
    );
    if (targetPage) {
      setActivePageId(targetPage.id);
      setSelectedSectionId(null);
    }
  };

  const isMobile = deviceMode === 'mobile' || testViewportWidth < 768;
  const isTablet = deviceMode === 'tablet' || (testViewportWidth >= 768 && testViewportWidth < 1024);

  const handleOpenImagePicker = (sectionId: string) => {
    setImagePickerTargetSectionId(sectionId);
    setIsImagePickerOpen(true);
  };

  const handleImageSelected = async (imageUrl: string, altText?: string) => {
    if (imagePickerTargetSectionId) {
      const section = pageSections.find((s) => s.id === imagePickerTargetSectionId);
      if (section) {
        updateSectionInActivePage(section.id, {
          content: {
            ...section.content,
            bgImageUrl: imageUrl,
            imageUrl: imageUrl,
            altText: altText || section.content?.altText,
          },
        });
      }
    } else {
      // General site logo
      await updateSiteData({
        settings: {
          ...(activeSite.settings || {}),
          logoUrl: imageUrl,
        },
      });
    }
    setIsImagePickerOpen(false);
  };

  const handleQuickAddService = async () => {
    const newService = {
      id: `service_${Date.now()}`,
      title: 'Yeni Kurumsal Hizmet',
      slug: `hizmet-${Date.now().toString().slice(-4)}`,
      shortDescription: 'İşletmenizin kurumsal standartlarda sunduğu yeni uzmanlık alanı ve çözümü.',
      icon: 'Briefcase',
      price: 'Teklif Alın',
      order: (activeSite.settings?.servicesList || []).length,
    };

    const existingServices = activeSite.settings?.servicesList || [];
    await updateSiteData({
      settings: {
        ...(activeSite.settings || {}),
        servicesList: [...existingServices, newService],
      },
    });
  };

  // Determine container width styling based on responsive device mode and inject scoped CSS isolation variables
  const getContainerWidthStyle = (): React.CSSProperties => {
    const siteTokens = getSiteThemeVariables(activeSite.settings);
    const baseStyle: React.CSSProperties = {
      ...siteTokens,
      '--site-primary': activeSite.settings?.primaryColor || '#2563EB',
      '--site-secondary': activeSite.settings?.secondaryColor || '#0F172A',
      '--site-accent': activeSite.settings?.accentColor || '#38BDF8',
      '--site-font': activeSite.settings?.fontFamily || 'inherit',
      '--site-heading-font': activeSite.settings?.headingFont || 'inherit',
      fontFamily: activeSite.settings?.fontFamily || undefined,
    } as React.CSSProperties;

    if (isMobile) return { ...baseStyle, width: '100%', maxWidth: '390px' };
    if (isTablet) return { ...baseStyle, width: '100%', maxWidth: '768px' };
    return { ...baseStyle, width: '100%', maxWidth: `${Math.min(testViewportWidth, 1440)}px` };
  };

  return (
    <div className="flex-1 bg-[#0B1324] overflow-y-auto overflow-x-hidden flex justify-center p-3 sm:p-6 lg:p-8 relative select-text custom-scrollbar">
      {/* Centered Website Canvas Card with Browser Frame Feel & CSS Stacking Isolation */}
      <div
        id="editor-canvas-device-container"
        style={getContainerWidthStyle()}
        className={`transition-all duration-300 relative flex flex-col website-renderer isolate contain-paint ${
          isMobile
            ? 'my-auto rounded-[36px] shadow-2xl border-[8px] border-[#162238] min-h-[740px] ring-1 ring-[#263750] overflow-hidden'
            : isTablet
            ? 'my-auto rounded-[24px] shadow-2xl border-[10px] border-[#162238] min-h-[860px] ring-1 ring-[#263750] overflow-hidden'
            : 'my-auto rounded-xl shadow-2xl border border-slate-200 min-h-full overflow-hidden'
        }`}
      >
        {/* Mobile Device Status Bar Notch simulation */}
        {isMobile && (
          <div className="flex h-6 bg-[#0B1324] border-b border-[#263750] items-center justify-between px-6 shrink-0 select-none text-[10px] text-[#94A3B8] font-mono">
            <span>09:41</span>
            <div className="w-20 h-3.5 bg-[#162238] rounded-full border border-[#263750] flex items-center justify-center">
              <div className="w-2 h-2 rounded-full bg-[#0B1324] ml-auto mr-1.5" />
            </div>
            <span>5G</span>
          </div>
        )}

        {/* Section List Render */}
        {pageSections.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center p-12 text-center text-slate-400 space-y-4 bg-slate-50 min-h-[400px]">
            <div className="w-12 h-12 rounded-2xl bg-blue-100 text-blue-600 flex items-center justify-center">
              <Sparkles className="w-6 h-6" />
            </div>
            <div>
              <h3 className="font-bold text-slate-800 text-base">Bu sayfada henüz bölüm bulunmuyor</h3>
              <p className="text-xs text-slate-500 mt-1 max-w-sm">
                Sayfanızı oluşturmaya başlamak için aşağıdaki butona tıklayarak ilk bölümünüzü ekleyin.
              </p>
            </div>
            <button
              type="button"
              onClick={() => onOpenAddSectionModal()}
              className="px-4 py-2 rounded-xl bg-[#2563EB] hover:bg-blue-600 text-white font-bold text-xs shadow-md transition flex items-center gap-2 active:scale-95"
            >
              <Plus className="w-4 h-4" />
              <span>İlk Bölümü Ekle</span>
            </button>
          </div>
        ) : (
          <div className="flex-1 divide-y divide-transparent pb-16">
            {pageSections.map((section, idx) => {
              const isSelected = selectedSectionId === section.id;
              return (
                <div
                  key={section.id}
                  data-section-id={section.id}
                  className={`relative transition-all group/sec ${
                    isSelected && !isPreviewMode
                      ? 'ring-2 ring-[#2563EB] z-20'
                      : !isPreviewMode
                      ? 'hover:ring-1 hover:ring-blue-400/60'
                      : ''
                  }`}
                  onClick={() => {
                    if (!isPreviewMode) {
                      setSelectedSectionId(section.id);
                    }
                  }}
                  onDoubleClick={() => {
                    if (!isPreviewMode) {
                      setSelectedSectionId(section.id);
                      if (section.type === 'HEADER') {
                        setIsHeaderSettingsOpen(true);
                      } else if (onOpenProperties) {
                        onOpenProperties();
                      }
                    }
                  }}
                >
                  {/* Floating Action Controls on Hover / Selection */}
                  {!isPreviewMode && (
                    <div
                      className={`absolute top-3 right-3 sm:right-5 z-30 transition-all duration-150 flex items-center gap-1 p-1 rounded-xl bg-[#162238]/95 text-[#F8FAFC] shadow-2xl backdrop-blur-md border border-[#263750] select-none ${
                        isSelected ? 'opacity-100 ring-2 ring-blue-500/40' : 'opacity-0 group-hover/sec:opacity-100'
                      }`}
                    >
                      {/* Move Up */}
                      <button
                        disabled={idx === 0}
                        onClick={(e) => {
                          e.stopPropagation();
                          moveSectionInActivePage(idx, idx - 1);
                        }}
                        title="Yukarı Taşı"
                        className="p-1.5 rounded-lg hover:bg-[#1D2B43] text-[#94A3B8] hover:text-white disabled:opacity-30 active:scale-90 transition"
                      >
                        <ChevronUp className="w-3.5 h-3.5" />
                      </button>

                      {/* Move Down */}
                      <button
                        disabled={idx === pageSections.length - 1}
                        onClick={(e) => {
                          e.stopPropagation();
                          moveSectionInActivePage(idx, idx + 1);
                        }}
                        title="Aşağı Taşı"
                        className="p-1.5 rounded-lg hover:bg-[#1D2B43] text-[#94A3B8] hover:text-white disabled:opacity-30 active:scale-90 transition"
                      >
                        <ChevronDown className="w-3.5 h-3.5" />
                      </button>

                      {/* Edit Trigger */}
                      {section.type === 'HEADER' ? (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setIsHeaderSettingsOpen(true);
                          }}
                          title="Header & Menü Ayarları"
                          className="px-2.5 py-1 rounded-lg bg-[#2563EB] hover:bg-blue-500 text-white text-[11px] font-bold flex items-center gap-1.5 shadow transition active:scale-95"
                        >
                          <Sliders className="w-3 h-3" />
                          <span className="hidden sm:inline">Menü & Logo</span>
                        </button>
                      ) : (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedSectionId(section.id);
                            if (onOpenProperties) onOpenProperties();
                          }}
                          title="Özellikleri Düzenle"
                          className="px-2.5 py-1 rounded-lg bg-[#2563EB] hover:bg-blue-500 text-white text-[11px] font-bold flex items-center gap-1.5 shadow transition active:scale-95"
                        >
                          <Edit3 className="w-3 h-3" />
                          <span className="hidden sm:inline">Düzenle</span>
                        </button>
                      )}

                      {/* Duplicate */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          duplicateSectionInActivePage(section.id);
                        }}
                        title="Bölümü Çoğalt"
                        className="p-1.5 rounded-lg hover:bg-[#1D2B43] text-[#94A3B8] hover:text-white active:scale-90 transition"
                      >
                        <Copy className="w-3.5 h-3.5" />
                      </button>

                      {/* Delete */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteSectionInActivePage(section.id);
                        }}
                        title="Bölümü Sil"
                        className="p-1.5 rounded-lg hover:bg-[#EF4444]/20 text-[#EF4444] hover:text-rose-200 active:scale-90 transition"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  )}

                  {/* Section Content */}
                  <SectionRenderer
                    section={section}
                    siteData={activeSite}
                    activePage={activePage}
                    settings={activeSite.settings}
                    industry={activeSite.industry}
                    isEditor={!isPreviewMode}
                    isSelected={isSelected}
                    forcePlayKey={animPreviewKeys[section.id]}
                    deviceMode={deviceMode}
                    onSelect={() => setSelectedSectionId(section.id)}
                    onOpenHeaderSettings={() => setIsHeaderSettingsOpen(true)}
                    onOpenImagePicker={() => handleOpenImagePicker(section.id)}
                    onAddService={handleQuickAddService}
                    onNavigate={handleCanvasNavigate}
                  />

                  {/* Add Section In Between Button on Selected State */}
                  {!isPreviewMode && isSelected && (
                    <div className="absolute -bottom-3 inset-x-0 z-20 flex items-center justify-center pointer-events-none">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          onOpenAddSectionModal(idx + 1);
                        }}
                        className="pointer-events-auto px-3.5 py-1 rounded-full bg-[#2563EB] hover:bg-blue-500 text-white text-[11px] font-bold shadow-lg flex items-center gap-1.5 transition transform active:scale-95 border border-blue-400/30"
                      >
                        <Plus className="w-3 h-3" />
                        <span>Araya Bölüm Ekle</span>
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Mandatory Platform Attribution (Fixed Platform Footer - Cannot be deleted/modified by customer) */}
        <PlatformAttribution
          bgColor={
            activePage?.sections?.find((s) => s.type === 'FOOTER' && s.isVisible !== false)?.style?.backgroundColor ||
            activePage?.sections?.filter((s) => s.isVisible !== false).slice(-1)[0]?.style?.backgroundColor
          }
        />

        {/* Mobile Sticky CTA */}
        {isMobile && <FloatingCtaBar siteData={activeSite} />}
      </div>

      {/* Header Settings Modal */}
      {isHeaderSettingsOpen && (
        <HeaderSettingsModal
          isOpen={isHeaderSettingsOpen}
          onClose={() => setIsHeaderSettingsOpen(false)}
          onOpenImagePicker={() => {
            setImagePickerTargetSectionId(null);
            setIsImagePickerOpen(true);
          }}
        />
      )}

      {/* Universal Image Picker Modal */}
      {isImagePickerOpen && (
        <ImagePickerModal
          isOpen={isImagePickerOpen}
          onClose={() => setIsImagePickerOpen(false)}
          onSelectImage={handleImageSelected}
        />
      )}
    </div>
  );
};
